import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, Observable, Subject, throwError } from 'rxjs';

import { PersonalDataResponseDto } from '@app/data/dtos/personal-data/personal-data/personal-data-response.dto';
import {
  PERSONAL_DATA_REPOSITORY,
  PersonalDataRepository
} from '@app/domain/repositories/personal-data/personal-data.repository.contract';
import { ErrorMsgEnum } from '@app/entities/enums/error/error-msg-enum';
import { PersonalDataCvSections } from '@app/entities/enums/ptrab/curriculum/curriculum.enum';
import { Knowledge } from '@app/entities/interfaces/personal-data/employee-languages.interface';
import { ModifyStudy } from '@app/entities/interfaces/personal-data/studies.interface';
import { TwoFactorAuthorization } from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { ErrorCodes } from '@app/entities/models/error/error.model';
import { PersonalDataAddress } from '@app/entities/models/personal-data/address.model';
import {
  CertificateEmployeeModel,
  EmployeeLanguages,
  LanguageMastersModel
} from '@app/entities/models/personal-data/employee-languages.model';
import { MaritalStatus } from '@app/entities/models/personal-data/marital-status.model';
import { PersonalData } from '@app/entities/models/personal-data/personal-data.model';
import {
  Qualification,
  QualificationEducationalLevels,
  QualificationStatuses,
  Study
} from '@app/entities/models/personal-data/studies.model';
import { UpdateEmergencyContact } from '@app/entities/models/personal-data/update-emergency-contact.model';
import { UpdatePhone } from '@app/entities/models/personal-data/update-phone.model';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';

import { PersonalDataUseCases } from './personal-data.use-cases.contract';

@Injectable({
  providedIn: 'root'
})
export class PersonalDataUseCasesImpl implements PersonalDataUseCases {
  #errorSubject: Subject<ErrorMsgEnum> = new Subject<ErrorMsgEnum>();
  error$: Observable<ErrorMsgEnum> = this.#errorSubject.asObservable();
  #personalData: PersonalDataRepository = inject(PERSONAL_DATA_REPOSITORY);

  getPersonalData(): Observable<PersonalData> {
    return this.#personalData.getPersonalData().pipe(catchError((err) => this.handleError(err)));
  }

  updateEmail(email: string, authorization: TwoFactorAuthorization): Observable<PersonalDataResponseDto> {
    return this.#personalData.updateEmail(email, authorization).pipe(catchError((err) => this.handleError(err)));
  }

  createPersonalPhone(
    phoneData: UpdatePhone,
    authorization: TwoFactorAuthorization
  ): Observable<PersonalDataResponseDto> {
    return this.#personalData
      .createPersonalPhone(phoneData, authorization)
      .pipe(catchError((err) => this.handleError(err)));
  }

  updatePersonalPhone(
    updatePhoneData: UpdatePhone,
    phoneId: string,
    authorization: TwoFactorAuthorization
  ): Observable<PersonalDataResponseDto> {
    return this.#personalData
      .updatePersonalPhone(updatePhoneData, phoneId, authorization)
      .pipe(catchError((err) => this.handleError(err)));
  }

  deletePhone(phoneSectionId: string): Observable<PersonalDataResponseDto> {
    return this.#personalData.deletePhone(phoneSectionId).pipe(catchError((err) => this.handleError(err)));
  }

  createEmergencyContact(emergencyContactData: UpdateEmergencyContact): Observable<PersonalDataResponseDto> {
    return this.#personalData
      .createEmergencyContact(emergencyContactData)
      .pipe(catchError((err) => this.handleError(err)));
  }

  updateEmergencyContact(
    emergencyContactData: UpdateEmergencyContact,
    phoneId: string
  ): Observable<PersonalDataResponseDto> {
    return this.#personalData
      .updateEmergencyContact(emergencyContactData, phoneId)
      .pipe(catchError((err) => this.handleError(err)));
  }

  updateAddress(
    address: PersonalDataAddress,
    authorization: TwoFactorAuthorization
  ): Observable<PersonalDataResponseDto> {
    return this.#personalData.updateAddress(address, authorization).pipe(catchError((err) => this.handleError(err)));
  }

  getMaritalStatusOptions(): Observable<MaritalStatus[]> {
    return this.#personalData.getMaritalStatusOptions().pipe(catchError((err) => this.handleError(err)));
  }

  updateMaritalStatus(id: string, authorization: TwoFactorAuthorization): Observable<object> {
    return this.#personalData.updateMaritalStatus(id, authorization).pipe(catchError((err) => this.handleError(err)));
  }

  getEmployeeLanguages(): Observable<MSafeAny> {
    return this.#personalData
      .getEmployeeLanguages()
      .pipe(catchError((err) => this.handleError(err, PersonalDataCvSections.LANGUAGES)));
  }

  getLanguagesMasters(): Observable<LanguageMastersModel> {
    return this.#personalData.getLanguagesMasters().pipe(catchError((err) => this.handleError(err)));
  }

  getLanguagesData(): Knowledge[] | undefined {
    return this.#personalData.getLanguagesData();
  }

  getStudiesData(): Study[] | undefined {
    return this.#personalData.getStudiesData();
  }

  getEmployeeStudies(): Observable<MSafeAny> {
    return this.#personalData
      .getEmployeeStudies()
      .pipe(catchError((err) => this.handleError(err, PersonalDataCvSections.STUDIES)));
  }

  getQualifications(
    countryIsoNumericCode: string,
    educationalLevelId: string,
    page: number,
    name?: string
  ): Observable<Qualification> {
    return this.#personalData
      .getQualifications(countryIsoNumericCode, educationalLevelId, page, name)
      .pipe(catchError((err) => this.handleError(err)));
  }

  getQualificationStatuses(): Observable<QualificationStatuses> {
    return this.#personalData.getQualificationStatuses().pipe(catchError((err) => this.handleError(err)));
  }

  getQualificationEducationalLevels(countryIsoNumericCode: string): Observable<QualificationEducationalLevels> {
    return this.#personalData
      .getQualificationEducationalLevels(countryIsoNumericCode)
      .pipe(catchError((err) => this.handleError(err)));
  }

  createEmployeeCertificate(certificate: CertificateEmployeeModel): Observable<EmployeeLanguages> {
    return this.#personalData.createEmployeeCertificate(certificate).pipe(catchError((err) => this.handleError(err)));
  }

  updateEmployeeCertificate(certificate: CertificateEmployeeModel): Observable<EmployeeLanguages> {
    return this.#personalData.updateEmployeeCertificate(certificate).pipe(catchError((err) => this.handleError(err)));
  }

  addStudyToEmployee(qualification: ModifyStudy): Observable<object> {
    return this.#personalData.addStudyToEmployee(qualification).pipe(catchError((err) => this.handleError(err)));
  }

  updateEmployeeStudy(qualification: ModifyStudy): Observable<object> {
    return this.#personalData.updateEmployeeStudy(qualification).pipe(catchError((err) => this.handleError(err)));
  }

  deleteEmployeeCertificate(languageId: string): Observable<object> {
    return this.#personalData.deleteEmployeeCertificate(languageId).pipe(catchError((err) => this.handleError(err)));
  }

  deleteStudy(studyId: string, countryIsoNumericCode: string): Observable<object> {
    return this.#personalData
      .deleteStudy(studyId, countryIsoNumericCode)
      .pipe(catchError((err) => this.handleError(err)));
  }

  private handleError = (error: HttpErrorResponse, section?: string): Observable<never> => {
    let errorType: ErrorMsgEnum = ErrorMsgEnum.SERVER_ERROR;

    if (section) {
      switch (error.status) {
        case ErrorCodes.LOCKED:
          errorType = ErrorMsgEnum.SERVICE_LOCKED;
          break;
        case ErrorCodes.SECTION_MAINTENANCE:
          errorType = ErrorMsgEnum.SECTION_MAINTENANCE;
          break;
        default:
          errorType = ErrorMsgEnum.SERVER_ERROR;
          break;
      }
    }

    this.#errorSubject.next(errorType);

    return throwError(() => error);
  };
}
